import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Form, DatePicker, TimePicker, Select } from "antd";
import moment from "moment/moment";
import Swal from "sweetalert2";
import Api from "../../utils/Api";
import { useEvent } from "../../contexts/EventContext";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState, Modifier } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from 'html-to-draftjs';
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { useCompany } from "../../contexts/CompanyContext";

export default function AddEvent({ edit = false, eventData = {}, role }) {
    const user = useSelector((state) => state.user.user);
    const { companies } = useCompany();

    const { fetchEvents } = useEvent();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [description, setDescription] = useState('');

    const [photoPreview, setPhotoPreview] = useState(eventData?.event_img ?? null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (edit && eventData) {
            setPhotoPreview(eventData.event_img)
            if (eventData.event_description) {
                setDescription(eventData.event_description)
                const blocksFromHtml = htmlToDraft(eventData.event_description);
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                const editorState = EditorState.createWithContent(contentState);
                setEditorState(editorState);
            }

            const uploadedByCompany = companies.find(company => company.id == eventData.event_uploaded_by);

            if (uploadedByCompany) {
                form.setFieldsValue({
                    event_uploaded_by: uploadedByCompany?.id
                });
            }

            form.setFieldsValue({
                // event_uploaded_by: companies.find(company => company.id == eventData.event_uploaded_by)?.companyname,
                event_title: eventData.event_title,
                event_description: eventData.event_description,
                short_description: eventData.short_description,
                event_date: eventData.event_date ? moment(eventData.event_date) : null,
                start_time: eventData.start_time ? moment(eventData.start_time, "HH:mm") : null,
                end_time: eventData.end_time ? moment(eventData.end_time, "HH:mm") : null,
                event_hall_no: eventData.event_hall_no,
                event_venue: eventData.event_venue,
                event_program: eventData.event_program,
                event_presentation: eventData.event_presentation,
                event_type: eventData.event_type,
                event_speaker: eventData.event_speaker,
            });
        }
    }, [edit, eventData, form]);

    const handleCancel = () => {
        navigate("/dashboard/events");
    };

    const base64ToFile = (base64String, fileName) => {
        // Split base64 to get the actual data
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1]; // Extract MIME type
        const bstr = atob(arr[1]); // Decode Base64
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        // Create a File object
        return new File([u8arr], fileName, { type: mime });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const maxSize = 11 * 1024 * 1024; // 10MB in bytes

            if (file.size > maxSize) {
                alert("File size should not exceed 10MB!");
                e.target.value = ""; // Reset input field
                return;
            }
            try {
                const compressedBase64String = await compressAndConvertToBase64(file);
                const base64Image = await compressImage(base64ToFile(compressedBase64String, "Event.jpg"));
                setPhotoPreview(base64Image); // Set the compressed image preview
            } catch (error) {
                console.error("Error processing image:", error);
                alert("Error processing image. Please try again.");
            }
        } else {
            setPhotoPreview(null); // Clear the preview if no file is selected
        }
    };

    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (event) => {
                img.src = event.target.result;
            };

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set desired dimensions, e.g., 1000x1000 pixels
                const maxWidth = 1000;
                const maxHeight = 1000;
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }

                // Resize canvas and draw the image
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                // Convert canvas to JPEG and get Base64 string
                const base64String = canvas.toDataURL('image/jpeg', 0.7); // Adjust quality as needed (0.7 is 70%)

                resolve(base64String);
            };

            img.onerror = (error) => {
                reject(error);
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsDataURL(file); // Start reading the file
        });
    };

    const compressAndConvertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (event) => {
                img.src = event.target.result;
            };

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                // Determine the size for a square crop
                const squareSize = Math.min(img.width, img.height);

                // Calculate the cropping area (centered)
                const cropX = (img.width - squareSize) / 2;
                const cropY = (img.height - squareSize) / 2;

                // Set canvas dimensions to the square size
                canvas.width = squareSize;
                canvas.height = squareSize;

                // Draw the cropped and resized image
                ctx.drawImage(
                    img,
                    cropX,
                    cropY,
                    squareSize,
                    squareSize, // Crop dimensions
                    0,
                    0,
                    squareSize,
                    squareSize // Final canvas dimensions
                );

                // Convert canvas to Base64 JPEG
                const base64String = canvas.toDataURL("image/jpeg", 0.7);
                resolve(base64String);
            };

            img.onerror = (error) => {
                reject(error);
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsDataURL(file);
        });
    };

    const handleFinish = async (values) => {
        setIsSubmitting(true);
        try {
            // if (!photoPreview) {
            //     Swal.fire({
            //         title: 'Error',
            //         text: 'Please select a photo',
            //         icon: 'error',
            //         confirmButtonText: 'OK',
            //         confirmButtonColor: '#000',
            //         iconColor: '#000',
            //     });
            //     return;
            // }
            const payload = {
                event_uploaded_by: values.event_uploaded_by || user.loggedinUserId,
                event_title: values.event_title,
                event_description: description,
                short_description: values.short_description,
                event_img: photoPreview,
                event_date: values.event_date.format("YYYY-MM-DD"),
                start_time: values.start_time.format("HH:mm"),
                end_time: values.end_time.format("HH:mm"),
                event_hall_no: values.event_hall_no,
                event_venue: values.event_venue,
                event_program: values.event_program,
                event_presentation: values.event_presentation,
                event_type: values.event_type,
                event_speaker: values.event_speaker,
            };
            let response;
            if (edit) {
                response = await Api.put(`/edit-event/${eventData.id}`, payload);
            } else {
                response = await Api.post('/add-event', payload);
            }
            if (response.status === 200) {
                Swal.fire({
                    title: 'Ereignis erfolgreich hinzugefügt',
                    text: 'Das Ereignis wurde erfolgreich hinzugefügt',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#000',
                    iconColor: '#000',
                });
                fetchEvents();
            }
            navigate("/dashboard/events");
        } catch (error) {
            console.error("Error submitting event:", error);
            Swal.fire({
                title: 'Fehler',
                text: 'Bitte versuchen Sie es erneut',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#000',
                iconColor: '#000'
            })
        } finally {
            setIsSubmitting(false);
        }
    };

    const CHARACTER_LIMIT = 2500;

    const toolbarOptions = {
        options: ['inline', 'blockType', 'history'],
        inline: {
            options: ['bold', 'italic', 'underline'],
        },
        blockType: {
            options: ['Normal', 'H4'],
        },
    };

    const editorLabels = {
        'components.controls.blocktype.h4': 'Überschrift',
        'components.controls.blocktype.normal': 'Normal',
    };

    const truncateContent = (content) => {
        return content.slice(0, CHARACTER_LIMIT);
    };

    const onEditorStateChange = (es) => {
        const plainText = es.getCurrentContent().getPlainText();

        if (plainText.length > CHARACTER_LIMIT) {
            const truncatedText = truncateContent(plainText);
            const newContentState = ContentState.createFromText(truncatedText);
            const newEditorState = EditorState.push(es, newContentState, 'change-block-data');

            setEditorState(newEditorState);
            setDescription(DOMPurify.sanitize(
                draftToHtml(convertToRaw(newContentState))
            ));
        } else {
            setEditorState(es);
            setDescription(DOMPurify.sanitize(
                draftToHtml(convertToRaw(es.getCurrentContent()))
            ));
        }
    };

    // const handlePastedText = (text, html, editorState, onChange) => {
    //     try {
    //         const currentContent = editorState.getCurrentContent();
    //         const selection = editorState.getSelection();

    //         // Ensure the new text is inserted at the current cursor position
    //         const contentStateWithInsertedText = Modifier.insertText(
    //             currentContent,
    //             selection,
    //             text
    //         );

    //         // Enforce character limit on the entire text
    //         const plainText = contentStateWithInsertedText.getPlainText();
    //         const truncatedText = truncateContent(plainText);

    //         // Create new content state with the truncated text
    //         const newContent = ContentState.createFromText(truncatedText);

    //         // Update the editor state with the new content
    //         const newState = EditorState.push(editorState, newContent, 'insert-characters');
    //         onChange(newState);

    //         return true; // Prevent default paste behavior 
    //     } catch (error) {
    //         console.error(error);
    //     }
    // };

    const handlePastedText = (text, html, editorState, onChange) => {
        try {
            const currentContent = editorState.getCurrentContent();
            const selection = editorState.getSelection();

            // Insert text at cursor position
            const contentStateWithInsertedText = Modifier.insertText(
                currentContent,
                selection,
                text
            );

            // Get the resulting plain text to check length
            const plainText = contentStateWithInsertedText.getPlainText();

            // If the text exceeds limit, don't apply the paste
            if (plainText.length > 2500) {
                // Optional: Show notification about character limit
                return true; // Prevent default paste behavior
            }

            // Create new editor state with the modified content
            const newState = EditorState.push(
                editorState,
                contentStateWithInsertedText,
                'insert-characters'
            );

            // Move cursor to end of inserted text
            const newSelection = newState.getSelection();
            const newEditorState = EditorState.forceSelection(newState, newSelection);

            onChange(newEditorState);
            return true; // Prevent default paste behavior
        } catch (error) {
            console.error(error);
            return false; // Allow default paste behavior as fallback
        }
    };

    return (
        <div style={{
            position: 'relative'
        }}>
            <img style={{
                width: '100%',
                objectFit: 'contain',
                position: 'fixed',
                zIndex: -1
            }} src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp" />
            <div
                style={{
                    position: "relative",
                    padding: "40px",
                    maxWidth: "1020px",
                    margin: "20px auto",
                    borderRadius: "20px",
                    backgroundColor: "rgb(28, 28, 28, 0.95)",
                    color: "#fff",
                }}
            >
                <h2 style={{ color: "#0BD349" }}>
                    {edit ? "Veranstaltung bearbeiten" : "Neue Veranstaltung erstellen"}
                </h2>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    style={{ marginTop: "20px" }}
                >
                    <Form.Item
                        label="Titel der Veranstaltung"
                        name="event_title"
                        rules={[{ required: true, message: "Bitte geben Sie den Titel ein" }]}
                    >
                        <Input placeholder="Titel der Veranstaltung" style={{ background: "#333", color: "#fff" }} />
                    </Form.Item>

                    <div style={{
                        margin: "20px 0 40px 0",
                    }}>
                        <label style={{ color: '#fff' }}>Beschreibung der Veranstaltung</label>
                        <div style={{ position: 'relative', marginTop: '10px' }}>
                            <Editor
                                placeholder="Beschreibung der Veranstaltung"
                                toolbar={toolbarOptions}
                                localization={{ locale: 'en', translations: editorLabels }}
                                editorState={editorState}
                                wrapperStyle={{
                                    color: '#fff',
                                    outline: '1px solid #fff'
                                }}
                                toolbarClassName="toolbar"
                                wrapperClassName="wrapper"
                                editorClassName="editor"
                                onEditorStateChange={onEditorStateChange}
                                handlePastedText={(text, html) => handlePastedText(text, html, editorState, setEditorState)}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    right: 5,
                                    bottom: 1,
                                    fontSize: 8,
                                    color: 'gray',
                                }}
                            >
                                Max. 2500 Zeichen
                            </span>
                        </div>
                    </div>

                    <Form.Item
                        style={{
                            marginBottom: '30px'
                        }}
                        label="Kurzbeschreibung"
                        name="short_description"
                        rules={[
                            { required: true, message: "Bitte geben Sie die Beschreibung ein" },
                            {
                                validator: (_, value) =>
                                    value && value.length > 100
                                        ? Promise.reject("Die Kurzbeschreibung darf maximal 100 Zeichen enthalten")
                                        : Promise.resolve(),
                            },
                        ]}
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder="Geben Sie eine kurze Beschreibung der Veranstaltung ein (maximal 100 Zeichen)"
                            style={{ background: "#333", color: "#fff" }}
                            maxLength={100} // Enforces the max length at the input level
                        />
                    </Form.Item>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "20px",
                            background: "#333",
                            color: "#fff",
                            padding: "20px",
                            borderRadius: "10px",
                            marginBottom: "20px",
                            border: '1px solid white'
                        }}
                    >
                        <label
                            style={{
                                cursor: "pointer",
                                padding: "10px 20px",
                                backgroundColor: "#0BD349",
                                color: "#000",
                                borderRadius: "5px",
                            }}
                        >
                            Bild hochladen
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                            />
                        </label>
                        <small
                            style={{
                                color: "#bbb",
                                fontSize: "0.9em",
                            }}
                        >
                            wird quadratisch zugeschnitten, max 10mb
                        </small>

                        {photoPreview ? (
                            <div style={{
                                position: 'relative'
                            }}>
                                <img
                                    src={photoPreview}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "300px",
                                        borderRadius: "10px",
                                    }}
                                />
                                <span onClick={() => {
                                    setPhotoPreview(null)
                                }} style={{
                                    position: 'absolute',
                                    top: '-15px',
                                    right: '-15px',
                                    cursor: 'pointer',
                                    padding: '6px 5px 0 5px',
                                    backgroundColor: 'red',
                                    color: '#fff',
                                    borderRadius: '5px'
                                }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                    </svg>
                                </span>
                            </div>
                        ) : (
                            <p style={{ color: "#fff" }}>Kein Bild ausgewählt</p>
                        )}
                    </div>

                    <Form.Item
                        label="Datum der Veranstaltung"
                        name="event_date"
                        rules={[{ required: true, message: "Bitte wählen Sie das Datum" }]}
                    >
                        <DatePicker
                            popupClassName="custom-popup"
                            style={{ width: "100%" }}
                            format="DD-MM-YYYY"
                            placeholder="Datum wählen"
                        />
                    </Form.Item>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '10px'
                    }}>
                        <Form.Item
                            style={{
                                flexGrow: 1,
                            }}
                            label="Startzeit"
                            name="start_time"
                            rules={[{ required: true, message: "Bitte wählen Sie die Startzeit" }]}
                        >
                            <TimePicker
                                popupClassName="custom-popup"
                                style={{ width: "100%" }}
                                format="HH:mm"
                                placeholder="Startzeit wählen"
                            />
                        </Form.Item>

                        <Form.Item
                            style={{
                                flexGrow: 1,
                            }}
                            label="Endzeit"
                            name="end_time"
                            rules={[{ required: true, message: "Bitte wählen Sie die Endzeit" }]}
                        >
                            <TimePicker
                                popupClassName="custom-popup"
                                style={{ width: "100%" }}
                                format="HH:mm"
                                placeholder="Endzeit wählen"
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Location (Stand und Nummer oder Location-Bezeichnung eintragen, max. 25 Zeichen)"
                        name="event_hall_no"
                        rules={[
                            { required: false, message: "Bitte geben Sie die Hallennummer oder Location-Bezeichnung ein" }
                        ]}
                    >
                        <Input
                            maxLength={25}
                            placeholder="Stand-Nr. oder Location-Bezeichnung"
                            style={{ background: "#333", color: "#fff" }}
                            type="text"
                        />
                    </Form.Item>

                    {/* <Form.Item
                        label="Halle Nr."
                        name="event_hall_no"
                        rules={[
                            { required: true, message: "Bitte geben Sie die Hallennummer ein" },
                            {
                                pattern: /^[1-9][0-9]{0,2}$/,
                                message: "Bitte geben Sie eine positive Zahl mit maximal 3 Ziffern ein"
                            }
                        ]}
                    >
                        <Input
                            placeholder="Halle Nr."
                            style={{ background: "#333", color: "#fff" }}
                            maxLength={3} // Limit the input to 3 characters
                            type="text" // Use text type to allow regex validation
                        />
                    </Form.Item> */}

                    {/* <Form.Item
                        label="Veranstaltungsort"
                        name="event_venue"
                        rules={[{ required: true, message: "Bitte geben Sie den Veranstaltungsort ein" }]}
                    >
                        <Input placeholder="Messezentrum Salzburg, Halle 10" style={{ background: "#333", color: "#fff" }} />
                    </Form.Item> */}

                    <Form.Item
                        className="custom-select-2"
                        dropdownStyle={{
                            backgroundColor: '#333',
                            color: '#fff',
                        }}
                        label="Programm"
                        name="event_program"
                        rules={[{ required: true, message: "Bitte geben Sie das Programm ein" }]}
                    >
                        <Select
                            popupClassName="custom-popup-select"
                            placeholder="Programm auswählen"
                        >
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="-">-</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="e-nnovation EXPERIENCE">e-nnovation EXPERIENCE</Select.Option>

                            {/* Admin-only options */}
                            {user.role === "super admin" && (
                                <>
                                    <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="e-nnovation SUMMIT">e-nnovation SUMMIT</Select.Option>
                                    <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="e-nnovation BRANCHENTALKS">e-nnovation BRANCHENTALKS</Select.Option>
                                    <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="e-nnovation AWARDS">e-nnovation AWARDS</Select.Option>
                                    <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="e-nnovation CLUB">e-nnovation CLUB</Select.Option>
                                </>
                            )}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Veranstaltungssprache"
                        name="event_presentation"
                        rules={[{ required: true, message: "Bitte geben Sie die Präsentation ein" }]}
                    >
                        <Input placeholder="Veranstaltungssprache" style={{ background: "#333", color: "#fff" }} />
                    </Form.Item>

                    <Form.Item
                        className="custom-select-2"
                        label="Art der Veranstaltung"
                        name="event_type"
                        rules={[{ required: true, message: "Bitte wählen Sie die Art der Veranstaltung" }]}
                    >
                        <Select
                            popupClassName="custom-popup-select"
                            placeholder="Art der Veranstaltung"
                        >
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Podiumsdiskussion">Podiumsdiskussion</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Produktpräsentation">Produktpräsentation</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Vortrag">Vortrag</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Webinar/Stream">Webinar/Stream</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Interview">Interview</Select.Option>
                            <Select.Option style={{ backgroundColor: '#333', color: '#fff' }} value="Presseveranstaltung">Presseveranstaltung</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Sprecher"
                        name="event_speaker"
                        rules={[{ required: true, message: "Bitte geben Sie den Sprecher ein" }]}
                    >
                        <Input placeholder="Sprecher" style={{ background: "#333", color: "#fff" }} />
                    </Form.Item>

                    {
                        user?.role === "super admin" && (
                            <Form.Item
                                className="custom-select-2"
                                dropdownStyle={{
                                    backgroundColor: '#333',
                                    color: '#fff',
                                }}
                                label="Adresse"
                                name="event_uploaded_by"
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    clearIcon={() => (
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{
                                            color: 'red'
                                        }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                    popupClassName="custom-popup-select"
                                    placeholder="Adresse auswählen"
                                    filterOption={(input, option) =>
                                        option?.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {companies.map((company) => (
                                        <Select.Option
                                            key={company.id}
                                            style={{ backgroundColor: '#333', color: '#fff' }}
                                            value={company.id}
                                        >
                                            {company.companyname}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )
                    }

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        <Button onClick={handleCancel} style={{ backgroundColor: "#fff", color: "#000" }}>
                            Abbrechen
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isSubmitting}
                            style={{
                                backgroundColor: "#0BD349",
                                color: "#000",
                                fontWeight: "bold",
                            }}
                        >
                            {isSubmitting ? "Einreichen..." : edit ? "Aktualisieren" : "Einreichen"}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
}